import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import { usePageMeta } from '@/hooks/usePageMeta';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { TurnstileWidget, type TurnstileWidgetHandle } from '@/components/security/TurnstileWidget';
import {
  Mail, Phone, Building2, MapPin, CheckCircle2, AlertCircle, Loader2,
} from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const HELP_TOPICS = [
  'Account / login help',
  'Billing & subscription',
  'Posting or bidding issues',
  'Messaging & notifications',
  'Technical problems',
  'Feature feedback',
];

type Errors = Partial<Record<'name' | 'email' | 'subject' | 'message', string>>;

const SupportPage: React.FC = () => {
  usePageMeta({
    fullTitle: true,
    title: 'LaneRunner Support — Contact Our Team',
    description:
      'Get help with your LaneRunner account, subscription, freight posting, bidding, or messaging. Contact LaneRunner support by email, phone, or the support form.',
  });

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState<Errors>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const set = (k: keyof typeof form) => (v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = (): boolean => {
    const e: Errors = {};
    if (!form.name.trim()) e.name = 'Please enter your name.';
    else if (form.name.trim().length > 100) e.name = 'Name must be under 100 characters.';
    if (!form.email.trim()) e.email = 'Please enter your email address.';
    else if (!EMAIL_RE.test(form.email.trim())) e.email = 'Please enter a valid email address.';
    if (!form.subject.trim()) e.subject = 'Please enter a subject.';
    else if (form.subject.trim().length > 140) e.subject = 'Subject must be under 140 characters.';
    if (!form.message.trim()) e.message = 'Please describe your issue.';
    else if (form.message.trim().length < 10) e.message = 'Please add a bit more detail (10+ characters).';
    else if (form.message.trim().length > 4000) e.message = 'Message must be under 4000 characters.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (sending || sent) return;
    setServerError(null);
    if (!validate()) return;

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('submit-public-support', {
        body: {
          name: form.name.trim(),
          email: form.email.trim(),
          subject: form.subject.trim(),
          message: form.message.trim(),
          turnstile_token: turnstileToken ?? '',
        },
      });

      if (error || !(data as { ok?: boolean } | null)?.ok) {
        setServerError(
          'We couldn\u2019t send your request right now. Please try again, or email support@lanerunner.com.',
        );
        setTurnstileToken(null);
        turnstileRef.current?.reset();
        return;
      }

      setSent(true);
    } catch {
      setServerError(
        'We couldn\u2019t send your request right now. Please try again, or email support@lanerunner.com.',
      );
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <LandingHeader />

      <main className="pt-28 sm:pt-32 pb-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <header className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              LaneRunner Support
            </h1>
            <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">
              Need help with your LaneRunner account, subscription, freight posting, bidding,
              messaging, or another app feature? Contact our support team and we&apos;ll help get
              you moving.
            </p>
          </header>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Contact info + topics */}
            <section className="lg:col-span-2 space-y-6">
              <div className="rounded-2xl border border-gray-200 bg-[#F5F7F8] p-5 sm:p-6">
                <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Contact Information
                </h2>
                <ul className="mt-4 space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <Mail className="w-4 h-4 mt-0.5 text-[#02857E] flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-gray-500">Email</div>
                      <a
                        href="mailto:support@lanerunner.com"
                        className="font-semibold text-gray-900 hover:text-[#02857E] break-words"
                      >
                        support@lanerunner.com
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Phone className="w-4 h-4 mt-0.5 text-[#02857E] flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-gray-500">Phone</div>
                      <div className="font-semibold text-gray-900">
                        201-977-9996
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Building2 className="w-4 h-4 mt-0.5 text-[#02857E] flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-gray-500">Company</div>
                      <div className="font-semibold text-gray-900">LaneRunner Inc.</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 mt-0.5 text-[#02857E] flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-gray-500">Address</div>
                      <div className="font-semibold text-gray-900">1854 NDSU Research Cir N, #5702, Fargo, ND 58102</div>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl border border-gray-200 p-5 sm:p-6">
                <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Common Help Topics
                </h2>
                <ul className="mt-4 space-y-2 text-sm text-gray-700">
                  {HELP_TOPICS.map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#02857E] flex-shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Form */}
            <section className="lg:col-span-3">
              <div className="rounded-2xl border border-gray-200 p-5 sm:p-8 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900">Contact Support</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Send us a message and our team will follow up by email.
                </p>

                {sent ? (
                  <div className="mt-6 rounded-xl border border-[#02857E]/30 bg-[#02857E]/5 p-5 flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#02857E] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-800">
                      Your support request has been sent. Our team will get back to you as soon as
                      possible.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="support-name">Name</Label>
                        <Input
                          id="support-name"
                          value={form.name}
                          maxLength={100}
                          autoComplete="name"
                          onChange={(e) => set('name')(e.target.value)}
                          aria-invalid={!!errors.name}
                          className="mt-1.5"
                        />
                        {errors.name && (
                          <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="support-email">Email</Label>
                        <Input
                          id="support-email"
                          type="email"
                          value={form.email}
                          maxLength={200}
                          autoComplete="email"
                          onChange={(e) => set('email')(e.target.value)}
                          aria-invalid={!!errors.email}
                          className="mt-1.5"
                        />
                        {errors.email && (
                          <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="support-subject">Subject</Label>
                      <Input
                        id="support-subject"
                        value={form.subject}
                        maxLength={140}
                        onChange={(e) => set('subject')(e.target.value)}
                        aria-invalid={!!errors.subject}
                        className="mt-1.5"
                      />
                      {errors.subject && (
                        <p className="mt-1 text-xs text-red-600">{errors.subject}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="support-message">Message</Label>
                      <Textarea
                        id="support-message"
                        value={form.message}
                        maxLength={4000}
                        rows={6}
                        onChange={(e) => set('message')(e.target.value)}
                        aria-invalid={!!errors.message}
                        className="mt-1.5"
                      />
                      <div className="mt-1 flex items-center justify-between gap-2">
                        {errors.message ? (
                          <p className="text-xs text-red-600">{errors.message}</p>
                        ) : (
                          <span />
                        )}
                        <span className="text-xs text-gray-400">{form.message.length}/4000</span>
                      </div>
                    </div>

                    <TurnstileWidget
                      ref={turnstileRef}
                      className="max-w-full"
                      onVerify={(t) => setTurnstileToken(t)}
                      onExpire={() => setTurnstileToken(null)}
                      onError={() => setTurnstileToken(null)}
                    />

                    {serverError && (
                      <div className="rounded-xl border border-red-200 bg-red-50 p-3 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-red-700">{serverError}</p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={sending}
                      className="w-full sm:w-auto rounded-full bg-[#02857E] hover:bg-[#026d67] text-white font-semibold px-6"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                        </>
                      ) : (
                        'Send Support Request'
                      )}
                    </Button>
                  </form>
                )}
              </div>

              <nav className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <Link to="/privacy" className="text-gray-600 hover:text-[#02857E]">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="text-gray-600 hover:text-[#02857E]">
                  Terms of Service
                </Link>
                <Link to="/" className="text-gray-600 hover:text-[#02857E]">
                  Home
                </Link>
              </nav>
            </section>
          </div>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
};

export default SupportPage;
